import React from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatic from 'hoist-non-react-statics';

/**
 * Replaces the wrapped component with the component used to construct this
 * function if the `isEditing` prop is `true`. This is useful for displaying
 * a completely different component in the place of the original one for editing
 * purposes.
 *
 * @param {(Object|Function)} optionsFn - the editing component or a function that generates the editing component
 * @param {Object=} editorProps - the props to pass into the editing component
 * @return {function(Component: Object): Object} the decorator or HOC to apply to the original component
 */
export default (optionsFn, editorProps) => (Component) => {
  const isReactComponent = component => React.Component.isPrototypeOf(component); // eslint-disable-line no-prototype-builtins

  class EditableWidget extends React.Component {
    static propTypes = {
      isEditing: PropTypes.bool.isRequired,
      editChannel: PropTypes.string,
    };

    static contextTypes = {
      store: PropTypes.object.isRequired,
    };

    getComponents() {
      let components = optionsFn;
      if (typeof optionsFn === 'function' && !isReactComponent(components)) {
        components = optionsFn(this.props, this.context.store);
      }

      // If we were passed a single component, stuff it into an object
      if (isReactComponent(components)) {
        components = { default: components };
      }

      return components;
    }

    getComponent(availableComponents) {
      const { editChannel } = this.props;

      let channel = 'default';
      if (editChannel && {}.hasOwnProperty.call(availableComponents, editChannel)) {
        channel = editChannel;
      }

      return availableComponents[channel];
    }

    renderEditing() {
      const components = this.getComponents();
      const component = this.getComponent(components);

      return React.createElement(component, { ...this.props, editorProps });
    }

    render() {
      const { isEditing } = this.props;

      if (isEditing) {
        return this.renderEditing();
      }

      return <Component {...this.props} />;
    }
  }

  EditableWidget.displayName = `EditableWidget(${Component.displayName || Component.name})`;
  return hoistNonReactStatic(EditableWidget, Component);
};
